import React, { createContext, useState, useContext, useEffect } from 'react';
import { blogService } from '../services/blogService';

const BlogContext = createContext();

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within BlogProvider');
  }
  return context;
};

export const BlogProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data from database API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('=== BlogContext: Fetching from DATABASE ===');
        
        // Fetch categories from database
        const categoriesData = await blogService.getCategories();
        console.log('Categories from database:', categoriesData);
        setCategories(categoriesData);
        
        // Fetch posts from database - use admin endpoint if in admin section
        const isAdminPage = window.location.pathname.includes('/admin/');
        console.log('Is admin page:', isAdminPage);
        
        let postsData;
        if (isAdminPage) {
          // Use admin endpoint for admin pages (shows all posts including drafts)
          postsData = await blogService.getAdminPosts();
          console.log('Posts from ADMIN API:', postsData);
        } else {
          // Use public endpoint for public pages (shows only published posts)
          postsData = await blogService.getPosts();
          console.log('Posts from public API:', postsData);
        }
        
        // Handle pagination response
        const postsArray = postsData.data || postsData;
        setPosts(postsArray);
        
        console.log(`✅ Loaded ${postsArray.length} posts and ${categoriesData.length} categories from database`);
        
      } catch (err) {
        console.error('❌ Error loading blog data from database:', err);
        setError('Failed to load blog data');
        
        // Fallback to empty arrays
        setPosts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Refresh data from database
  const refreshData = async () => {
    try {
      const categoriesData = await blogService.getCategories();
      setCategories(categoriesData);
      
      // Use admin endpoint if in admin section
      const isAdminPage = window.location.pathname.includes('/admin/');
      
      let postsData;
      if (isAdminPage) {
        postsData = await blogService.getAdminPosts();
      } else {
        postsData = await blogService.getPosts();
      }
      
      const postsArray = postsData.data || postsData;
      setPosts(postsArray);
      
      console.log('✅ Data refreshed from database');
    } catch (err) {
      console.error('❌ Error refreshing data:', err);
    }
  };

  // Create new post
  const createPost = async (postData) => {
    try {
      const result = await blogService.createPost(postData);
      // Ensure posts is an array before spreading
      const currentPosts = Array.isArray(posts) ? posts : [];
      setPosts([...currentPosts, result.data]);
      return result.data;
    } catch (err) {
      console.error('❌ Error creating post:', err);
      throw err;
    }
  };

  // Update post
  const updatePost = async (id, postData) => {
    try {
      const result = await blogService.updatePost(id, postData);
      // Ensure posts is an array before mapping
      const currentPosts = Array.isArray(posts) ? posts : [];
      setPosts(currentPosts.map(post => post.id === id ? result.data : post));
      return result.data;
    } catch (err) {
      console.error('❌ Error updating post:', err);
      throw err;
    }
  };

  // Delete post
  const deletePost = async (id) => {
    try {
      await blogService.deletePost(id);
      // Ensure posts is an array before filtering
      const currentPosts = Array.isArray(posts) ? posts : [];
      setPosts(currentPosts.filter(post => post.id !== id));
    } catch (err) {
      console.error('❌ Error deleting post:', err);
      throw err;
    }
  };

  // Get post by slug
  const getPostBySlug = (slug) => {
    // Ensure posts is an array before finding
    if (!Array.isArray(posts)) {
      console.warn('Posts is not an array:', posts);
      return null;
    }
    return posts.find(post => post.slug === slug);
  };

  // Get published posts
  const getPublishedPosts = () => {
    // Ensure posts is an array before filtering
    if (!Array.isArray(posts)) {
      console.warn('Posts is not an array:', posts);
      return [];
    }
    return posts.filter(post => post.status === 'published' || post.published === true);
  };

  // Add category
  const addCategory = async (categoryData) => {
    try {
      // categoryData may be a simple name string (from Categories.js) or an object
      const payload = typeof categoryData === 'string'
        ? { name: categoryData, description: '' }
        : categoryData;

      const result = await blogService.createCategory(payload);
      setCategories([...categories, result.data]);
      return result.data;
    } catch (err) {
      console.error('❌ Error creating category:', err);
      throw err;
    }
  };

  // Update category
  const updateCategory = async (id, categoryData) => {
    try {
      const result = await blogService.updateCategory(id, categoryData);
      setCategories(categories.map(cat => cat.id === id ? result.data : cat));
      return result.data;
    } catch (err) {
      console.error('❌ Error updating category:', err);
      throw err;
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    try {
      await blogService.deleteCategory(id);
      setCategories(categories.filter(cat => cat.id !== id));
    } catch (err) {
      console.error('❌ Error deleting category:', err);
      throw err;
    }
  };

  const value = {
    posts,
    categories,
    loading,
    error,
    createPost,
    updatePost,
    deletePost,
    getPostBySlug,
    getPublishedPosts,
    addCategory,
    updateCategory,
    deleteCategory,
    refreshData
  };

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};
